import React, { useEffect, useState } from 'react';
import { Alert, Card, Space, Spin, Tag, Typography } from 'antd';
import { Link } from 'react-router-dom';
import { vendorApi } from '@/utils/vendorApi';
import { useAuthStore } from '@/store/authStore';
import { canEnterOperatorVendorDirectory } from '@/store/vendorAccess';
import { matchesSelectedBu } from '@/utils/businessUnits';
import { VendorItem } from '@/types';

const { Title, Text } = Typography;

const PendingVendorsCard: React.FC = () => {
  const { authorization, currentBusinessUnit } = useAuthStore();
  const [rows, setRows] = useState<VendorItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [moreAvailable, setMoreAvailable] = useState(false);
  const isAdmin = Boolean(authorization?.globalAdmin);

  useEffect(() => {
    const current = useAuthStore.getState().authorization;
    const allowed = Boolean(current?.globalAdmin) || canEnterOperatorVendorDirectory(current);
    if (!allowed) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const response = current?.globalAdmin
          ? await vendorApi.listVendors({ status: 'pending' })
          : await vendorApi.listOperatorVendorsPage({ status: 'pending' });
        if (!cancelled) {
          setRows(response.vendors);
          setMoreAvailable(Boolean(response.nextToken));
        }
      } catch {
        if (!cancelled) {
          setFailed(true);
          setRows([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedId = currentBusinessUnit?.id;
  const visible = (selectedId
    ? rows.filter((row) => matchesSelectedBu(row.bu, selectedId, authorization?.catalog))
    : rows
  ).slice(0, 5);
  const pendingPath = isAdmin ? '/vendors?status=pending' : '/my-vendors?status=pending';

  return (
    <Card>
      <Title level={4} style={{ marginTop: 0 }}>Vendors pending onboarding</Title>
      {loading ? (
        <Spin />
      ) : failed ? (
        <Alert type="error" showIcon message="Couldn't load vendors." />
      ) : visible.length === 0 ? (
        <Text type="secondary">No vendors are pending onboarding.</Text>
      ) : (
        <Space direction="vertical" style={{ width: '100%' }}>
          {visible.map((row) => (
            <div key={`${row.vendorId}::${row.hostname || 'default'}`}>
              <Text>{row.vendorId}</Text>
              <Tag color="gold" style={{ marginLeft: 8 }}>Pending</Tag>
            </div>
          ))}
        </Space>
      )}
      {!loading && moreAvailable && !failed && (
        <Text type="secondary" style={{ display: 'block', marginTop: 12 }}>
          Showing the latest results.
        </Text>
      )}
      {!loading && (
        <div style={{ marginTop: 16 }}>
          <Link to={pendingPath}>View pending vendors</Link>
        </div>
      )}
    </Card>
  );
};

export default PendingVendorsCard;
